import { useContext, useMemo } from 'react';
import { AsyncStatus } from 'reporting-data/asyncData/AsyncStatus';
import { UserInfoContext } from 'reporting-data/userInfo/userInfoContext';
import { FieldSources } from 'reporting-snowflake/relational/schema/column-records';
import { ChannelNames, VisualTypes } from 'reporting-snowflake/relational/schema/visual-records';
import { createDefaultMeasure, setColumnAggregation } from 'reporting-snowflake/relational/utils/column-utils';
import { createRelationalReportFromBaseReport, removeColumnFromColumns } from 'reporting-snowflake/relational/utils/report-utils';
import { getDataSourceIdFromTableDescription } from 'reporting-snowflake/relational/utils/table-utils';
import useMeta from 'reporting-ui-components/components/snowflake/internal/useMeta';
import { STANDARD_FIELD_TYPES } from 'reporting-ui-components/lib/report-strategies/shared/constants/fieldTypes';
import { getStandardFieldType } from 'reporting-ui-components/lib/report-strategies/shared/utils/reportGetters';
import { filterDuplicateObjectsFromArray } from '../../../../../../../lib/objectUtils';
import { reportConfigProcessor } from '../../../../insights/utils';
import { NUMBER_OF_REPORTS_IN_CAROUSEL } from '../constants';
import { addColumnToChannel, updateVisualType
// @ts-expect-error Untyped dependency
} from 'reporting-snowflake/relational/updaters/report';
const splitMetricsIntoGroups = allMetrics => new Array(Math.ceil(allMetrics.length / NUMBER_OF_REPORTS_IN_CAROUSEL)).fill(0).map((_, index) => {
  const startIndex = index * NUMBER_OF_REPORTS_IN_CAROUSEL;
  const endIndex = startIndex + NUMBER_OF_REPORTS_IN_CAROUSEL;
  return allMetrics.slice(startIndex, endIndex);
});
const getMetricFieldFromRelationalReport = (reportDefinition, metric) => {
  const columns = reportDefinition.get('columns');
  const table = reportDefinition.get('table');
  const metricColumn = columns.find(column => column.get('role') === 'MEASURE' && column.getIn(['field', 'name']) === metric.property && getDataSourceIdFromTableDescription(table, column.getIn(['field', 'table'])) === metric.dataType);
  return metricColumn === null || metricColumn === void 0 ? void 0 : metricColumn.get('field');
};
const getReportHasLimitsForQuery = report => {
  var _getIn;
  return !!((_getIn = report.getIn(['reportDefinition', 'limitsForQuery'])) !== null && _getIn !== void 0 && _getIn.size);
};
const getRelationalReportHasExpressionMetric = report => {
  const columns = report.getIn(['reportDefinition', 'columns']);
  return !!columns.find(column => column.getIn(['field', 'source']) === FieldSources.EXPRESSION);
};
const combineReportMetricsWithReportDefinition = (reportDefinition, reportMeta, userInfo, metrics) => {
  return metrics.reduce((currentReport, metric) => {
    const metricType = metric.metricType;
    if (!metricType) {
      return currentReport;
    }
    const metricField = getMetricFieldFromRelationalReport(reportDefinition, metric);
    const newMeasureColumn = setColumnAggregation(createDefaultMeasure(metricField), metricType);
    return addColumnToChannel(updateVisualType(currentReport, {
      type: VisualTypes.TEXT,
      deps: {
        meta: reportMeta.data
      }
    }), {
      column: newMeasureColumn,
      channelName: ChannelNames.VALUES,
      deps: {
        meta: reportMeta.data,
        userInfo
      }
    });
  }, reportDefinition);
};
const useFetchRelationalReportsForKPIContent = (previewReport, kpiLimit) => {
  const userInfo = useContext(UserInfoContext);
  const processedReport = useMemo(() => reportConfigProcessor(previewReport), [previewReport]);
  const reportDef = useMemo(() => {
    let report = createRelationalReportFromBaseReport(processedReport);
    report = report.get('columns').reduce((acc, column) => {
      if (column.get('role') === 'DIMENSION') {
        return removeColumnFromColumns(acc, column);
      }
      return acc;
    }, report);
    return report;
  }, [processedReport]);
  const reportMeta = useMeta(reportDef);
  return useMemo(() => {
    if (reportMeta.loading) {
      return {
        status: AsyncStatus.STARTED
      };
    }
    if (reportMeta.error) {
      return {
        status: AsyncStatus.FAILED,
        error: reportMeta.error
      };
    }
    const metrics = filterDuplicateObjectsFromArray(getStandardFieldType(processedReport, STANDARD_FIELD_TYPES.METRICS));
    const limitedMetrics = kpiLimit ? metrics.slice(0, kpiLimit) : metrics;
    const groupedMetricsForCarousel = splitMetricsIntoGroups(limitedMetrics);
    const originalAggregationColumns = reportDef.get('columns').filter(column => column.get('role') === 'MEASURE');
    const reportForDatawellProcessing = updateVisualType(reportDef, {
      type: VisualTypes.TEXT,
      deps: {
        meta: reportMeta.data,
        userInfo
      }
    });
    if (getStandardFieldType(processedReport, STANDARD_FIELD_TYPES.CHART_TYPE) === VisualTypes.COMBO || getReportHasLimitsForQuery(processedReport) || getRelationalReportHasExpressionMetric(processedReport)) {
      return {
        status: AsyncStatus.SUCCEEDED,
        data: [processedReport.set('reportDefinition', reportForDatawellProcessing)]
      };
    }
    const relationalReportsForCarousel = groupedMetricsForCarousel.map(metricGroup => {
      const newReportDef = combineReportMetricsWithReportDefinition(reportForDatawellProcessing, reportMeta, userInfo, metricGroup);
      const reportWithoutOriginalMetrics = originalAggregationColumns.reduce((acc, column) => {
        return removeColumnFromColumns(acc, column);
      }, newReportDef);
      return processedReport.set('reportDefinition', reportWithoutOriginalMetrics);
    });
    return {
      status: AsyncStatus.SUCCEEDED,
      data: relationalReportsForCarousel
    };
  }, [kpiLimit, processedReport, reportMeta, reportDef, userInfo]);
};
export default useFetchRelationalReportsForKPIContent;